/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Link2, Loader2, MoreVertical, X } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useApplications } from '../services/queries';
import {
  useCreateApplication,
  useUpdateApplication,
  useSetStatusApplication,
  useDeleteApplication,
  useUploadApplicationImage,
} from '../services/mutations';
import FormDrawer from '../components/FormDrawer';
import ConfirmationModal from '../components/ConfirmationModal';
import toast from 'react-hot-toast';
import useAuth from '../hooks/useAuth';

const initialFormState = {
  code: '',
  name: '',
  host: '',
  auth_uri: '/',
  health_uri: '/health',
  description: '',
  is_active: true,
};

const Applications = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { isLoading, data, error, refetch } = useApplications();
  const createApplication = useCreateApplication();
  const updateApplication = useUpdateApplication();
  const setStatusApplication = useSetStatusApplication();
  const deleteApplication = useDeleteApplication();
  const uploadApplication = useUploadApplicationImage();
  const { checkPermissionElement } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(initialFormState);
  const [editMode, setEditMode] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [formError, setFormError] = useState(null);
  const [localData, setLocalData] = useState([]);
  const [loading, setLoading] = useState({
    submit: false,
    delete: false,
    toggle: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (data) {
      setLocalData(data.data);
    }
  }, [data]);

  // Cleanup preview URL saat unmount
  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Cleanup saat drawer ditutup
  useEffect(() => {
    if (!isOpen) {
      setImageFile(null);
      setImagePreview(null);
      setFormError(null);
    }
  }, [isOpen]);

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleStatusChange = (value) => {
    setForm((prevForm) => ({ ...prevForm, is_active: value === 'true' }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validasi tipe file
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        e.target.value = ''; // Reset input
        return;
      }
      
      // Validasi ukuran file (maks 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        e.target.value = ''; // Reset input
        return;
      }
      
      setImageFile(file);
      
      // Buat preview
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleClearImage = () => {
    setImageFile(null);
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setLoading((prevLoading) => ({ ...prevLoading, submit: true }));

    const {
      code,
      name,
      host,
      auth_uri,
      health_uri,
      description,
      is_active,
    } = form;

    const payload = {
      code,
      name,
      host,
      auth_uri,
      health_uri,
      description,
      is_active,
    };

    try {
      if (editMode) {
        // Separate status update from other fields
        const { is_active: status, ...updateBody } = payload;
        
        // Update other fields
        await updateApplication.mutateAsync({
          code: selectedCode,
          body: updateBody,
        });
        
        // Update status separately using the status endpoint
        await setStatusApplication.mutateAsync({
          code: selectedCode,
          is_active: Boolean(status),
        });
        
        // Upload image jika ada file baru
        if (imageFile) {
          await uploadApplication.mutateAsync({
            code: selectedCode,
            file: imageFile,
          });
        }
      } else {
        // Create application dulu
        await createApplication.mutateAsync(payload);
        
        // Upload image jika ada file
        if (imageFile) {
          await uploadApplication.mutateAsync({
            code: payload.code,
            file: imageFile,
          });
        }
      }
      setIsOpen(false);
      setForm(initialFormState);
      refetch();
      toast.success(editMode ? 'Application updated successfully' : 'Success add new application');
    } catch (error) {
      setFormError(error.message);
      toast.error(editMode ? 'Failed to update application' : 'Failed to create application');
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, submit: false }));
    }
  };

  const handleDelete = useCallback((code) => {
    setCodeToDelete(code);
    setIsModalOpen(true);
  }, []);

  const confirmDelete = async () => {
    setLoading((prevLoading) => ({ ...prevLoading, delete: true }));
    try {
      await deleteApplication.mutateAsync(codeToDelete);
      setIsModalOpen(false);
      refetch();
    } catch (error) {
      console.error('Error during application delete:', error);
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, delete: false }));
    }
  };

  const handleToggleStatus = useCallback(
    async (code, is_active) => {
      setLoading((prevLoading) => ({ ...prevLoading, toggle: true }));
      const previousData = localData;
      const updatedData = localData.map((app) =>
        app.code === code ? { ...app, is_active: !is_active } : app,
      );
      setLocalData(updatedData);

      try {
        await setStatusApplication.mutateAsync({ code, is_active: !is_active });
        refetch();
        toast.success('Status aplikasi diperbarui');
      } catch (error) {
        console.error('Error during status toggle:', error);
      } finally {
        setLoading((prevLoading) => ({ ...prevLoading, toggle: false }));
      }
    },
    [localData, setStatusApplication, refetch],
  );

  const handleEdit = useCallback((application) => {
    setForm({
      ...application,
      is_active: application.is_active ?? true,
    });
    setSelectedCode(application.code);
    setEditMode(true);
    // Tampilkan gambar yang sudah ada jika ada
    setImagePreview(application.img_url || null);
    setImageFile(null); // Reset file, user harus pilih ulang jika mau ganti
    setIsOpen(true);
  }, []);

  const handleRowClick = useCallback(
    (code) => {
      navigate(`/applications/${code}`);
    },
    [navigate],
  );

  // Pagination handlers
  const handlePageChange = (value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!localData) return [];
    if (!searchQuery.trim()) return localData;

    const query = searchQuery.toLowerCase();
    return localData.filter(
      (app) =>
        app.name.toLowerCase().includes(query) ||
        app.code.toLowerCase().includes(query) ||
        app.host.toLowerCase().includes(query),
    );
  }, [localData, searchQuery]);

  // Get paginated data and total pages
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, page, rowsPerPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredData.length / rowsPerPage);
  }, [filteredData, rowsPerPage]);

  // Generate pagination items
  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>,
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis-start">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            isActive={i === page}
          >
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <PaginationItem key="ellipsis-end">
            <span className="flex h-9 w-9 items-center justify-center">
              ...
            </span>
          </PaginationItem>,
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return items;
  };

  const renderTableRows = useMemo(
    () =>
      paginatedData.map((el, i) => (
        <TableRow
          key={el.code}
          onClick={() => handleRowClick(el.code)}
          className="cursor-pointer hover:bg-muted/50"
        >
          <TableCell width="70" className="border-b-2 border-gray-200">
            {(page - 1) * rowsPerPage + i + 1}
          </TableCell>
          <TableCell className="border-b-2 border-gray-200">
            <div className="flex items-center gap-2">
              <div className="flex flex-col">
                <span className="font-medium">{el.name}</span>
                <span className="flex items-center text-xs text-muted-foreground gap-1">
                  <Link2 className="h-3 w-3" />
                  {el.host}
                </span>
              </div>
            </div>
          </TableCell>
          <TableCell className="border-b-2 border-gray-200">
            <Badge variant="secondary" className="text-xs">
              {el.code}
            </Badge>
          </TableCell>
          <TableCell className="border-b-2 border-gray-200">
            <Badge
              variant={el.is_active ? 'secondary' : 'outline'}
              className={`text-xs ${
                el.is_active
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : ''
              }`}
            >
              {el.is_active ? 'Aktif' : 'Nonaktif'}
            </Badge>
          </TableCell>
          <TableCell
            className="sticky-action w-[60px]"
            onClick={(e) => e.stopPropagation()}
          >
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 sm:h-9 sm:w-9"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-40 p-1" align="end">
                <div className="flex flex-col gap-0.5">
                  {checkPermissionElement(
                    'superapp:application-management:button:edit',
                  ) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(el)}
                      className="justify-start h-8 px-2"
                    >
                      <Edit className="size-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  {checkPermissionElement(
                    'superapp:application-management:button:delete',
                  ) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(el.code)}
                      className="justify-start h-8 px-2 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="size-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </TableCell>
        </TableRow>
      )),
    [
      paginatedData,
      handleEdit,
      handleDelete,
      handleRowClick,
      page,
      rowsPerPage,
      checkPermissionElement,
    ],
  );

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold">Application Management</h1>
          <p className="muted-text">
            Kelola daftar aplikasi dan status koneksi
          </p>
        </div>
        {checkPermissionElement(
          'superapp:application-management:button:create',
        ) && (
          <Button
            onClick={() => {
              setForm(initialFormState);
              setEditMode(false);
              handleClearImage();
              setIsOpen(true);
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Application
          </Button>
        )}
      </div>

      <div className="section-card p-6">
        <div className="flex gap-4 mb-4 items-end flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="search" className="mb-2 block">
              Search
            </Label>
            <Input
              id="search"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by name, code, or URL..."
              className="w-full"
            />
          </div>
          <div>
            <Button
              variant="outline"
              onClick={handleClearSearch}
              className="h-10"
            >
              Clear
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        {isLoading ? (
          <div className="flex justify-center items-center mt-20">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            <div className="table-card">
              <div className="table-wrapper">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[70px] font-bold border-b-2 border-gray-200">
                        No
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Application Name
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Code
                      </TableHead>
                      <TableHead className="font-bold border-b-2 border-gray-200">
                        Status
                      </TableHead>
                      <TableHead className="sticky-action w-[60px] font-bold border-b-2 border-gray-200">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>{renderTableRows}</TableBody>
                </Table>
              </div>
            </div>

            <div className="flex justify-between items-center mt-6 flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Jumlah per halaman:
                </span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) =>
                    handleRowsPerPageChange(Number(value))
                  }
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => handlePageChange(Math.max(1, page - 1))}
                        className={
                          page === 1
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>
                    {getPaginationItems()}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() =>
                          handlePageChange(Math.min(totalPages, page + 1))
                        }
                        className={
                          page === totalPages
                            ? 'pointer-events-none opacity-50'
                            : 'cursor-pointer'
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </>
        )}
      </div>

      <FormDrawer isOpen={isOpen} setIsOpen={setIsOpen}>
        <form onSubmit={handleSubmit} className="w-full max-w-[500px] flex flex-col h-full">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold">
              {editMode ? 'Edit Application' : 'Add Application'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {editMode ? 'Update application details and settings.' : 'Create a new application.'}
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Application Name</Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChangeInput}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                name="code"
                value={form.code}
                onChange={handleChangeInput}
                required
                disabled={editMode}
                className={editMode ? "bg-muted text-muted-foreground" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="host">Application URL</Label>
              <Input
                id="host"
                name="host"
                value={form.host}
                onChange={handleChangeInput}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="auth_uri">Login Path</Label>
              <Input
                id="auth_uri"
                name="auth_uri"
                value={form.auth_uri}
                onChange={handleChangeInput}
                // required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="health_uri">Health URI</Label>
              <Input
                id="health_uri"
                name="health_uri"
                value={form.health_uri}
                onChange={handleChangeInput}
                // required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleChangeInput}
                rows={4}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            
            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label htmlFor="image">Application Image</Label>
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              {imagePreview && (
                <div className="mt-3 space-y-2">
                  <div className="relative w-32 h-32 border rounded-md overflow-hidden bg-muted flex items-center justify-center">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClearImage}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Remove Image
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Upload an image for the application (max 5MB, supports: JPG, PNG, GIF)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.is_active ? 'true' : 'false'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[1400]">
                  <SelectItem value="true">Aktif</SelectItem>
                  <SelectItem value="false">Nonaktif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-6 border-t bg-muted/10 flex justify-end gap-3">
            <Button variant="ghost" type="button" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading.submit}>
              {loading.submit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editMode ? 'Update Application' : 'Add Application'}
            </Button>
          </div>
        </form>
      </FormDrawer>

      <ConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        description="Are you sure you want to delete this application?"
      />
    </div>
  );
};

export default Applications;
